import { useState } from 'react';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Text,
    useColorModeValue,
    VStack,
} from '@chakra-ui/react';
import SignUp from './SignUp';


// Function to hash the input password for comparison
const hashPassword = async (password: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
};

function ProtectedSignUp() {
    const [accessPassword, setAccessPassword] = useState('');
    // const [isAuthenticated, setIsAuthenticated] = useState(() => {
    //     // Check if user was previously authenticated
    //     return localStorage.getItem('registrationAccess') === 'granted';
    // });
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [error, setError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);

    const textColor = useColorModeValue('gray.400', 'white');
    const bgColor = useColorModeValue('white', 'gray.700');

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsVerifying(true);
        setError('');

        try {
            // Hash the entered password
            const hashedInput = await hashPassword(accessPassword);

            // Compare with stored hash (constant-time comparison to prevent timing attacks)
            if (hashedInput === process.env.REACT_APP_REGISTRATION_PASSWORD_HASH) {
                setIsAuthenticated(true);
                // Store authentication in localStorage
                localStorage.setItem('registrationAccess', 'granted');
            } else {
                setError('Incorrect password. Access denied.');
                setAccessPassword('');
            }
        } catch (err) {
            setError('An error occurred during verification.');
            setAccessPassword('');
        } finally {
            setIsVerifying(false);
        }
    };

    // If authenticated, show the actual sign-up form
    if (isAuthenticated) {
        return <SignUp />;
    }

    // Show password protection screen
    return (
        <Flex
            direction="column"
            alignSelf="center"
            justifySelf="center"
            overflow="hidden"
            minH="100vh"
            bg="gray.500"
        >
            <Box
                position="absolute"
                minH={{ base: '70vh', md: '50vh' }}
                w={{ md: 'calc(100vw - 50px)' }}
                borderRadius={{ md: '15px' }}
                left="0"
                right="0"
                bgRepeat="no-repeat"
                overflow="hidden"
                zIndex="-1"
                top="0"
                bgImage={'url(/assets/img/BgSignUp.jpg)'}
                bgSize="cover"
                mx={{ md: 'auto' }}
                mt={{ md: '14px' }}
            />

            <Flex direction="column" textAlign="center" justifyContent="center" align="center" mt="6.5rem" mb="30px">
                <Text fontSize="4xl" color="white" fontWeight="bold">
                    🔒 Restricted Access
                </Text>
                <Text
                    fontSize="md"
                    color="white"
                    fontWeight="normal"
                    mt="10px"
                    mb="26px"
                    w={{ base: '90%', sm: '60%', lg: '40%', xl: '30%' }}
                >
                    This registration page is protected. Please enter the access password to continue.
                </Text>
            </Flex>

            <Flex alignItems="center" justifyContent="center" mb="60px" mt="20px">
                <Flex
                    direction="column"
                    w="445px"
                    background="transparent"
                    borderRadius="15px"
                    p="40px"
                    mx={{ base: '100px' }}
                    bg={bgColor}
                    boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)"
                >
                    <VStack spacing={6}>
                        <Heading fontSize="2xl" color={textColor} textAlign="center">
                            🔐 Password Required
                        </Heading>

                        <Text fontSize="sm" color={textColor} textAlign="center">
                            Enter the secret password to access the registration form
                        </Text>

                        <form onSubmit={handlePasswordSubmit} style={{ width: '100%' }}>
                            <VStack spacing={4} w="100%">
                                <FormControl isRequired>
                                    <FormLabel ms="4px" fontSize="sm" fontWeight="normal">
                                        Access Password
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        ms="4px"
                                        borderRadius="15px"
                                        type="password"
                                        placeholder="Enter secret password"
                                        size="lg"
                                        value={accessPassword}
                                        onChange={(e) => setAccessPassword(e.target.value)}
                                        autoFocus
                                        disabled={isVerifying}
                                    />
                                </FormControl>

                                {error && (
                                    <Text color="red.500" fontSize="sm" textAlign="center">
                                        {error}
                                    </Text>
                                )}

                                <Button
                                    type="submit"
                                    colorScheme="brand"
                                    fontSize="10px"
                                    fontWeight="bold"
                                    w="100%"
                                    h="45"
                                    mt="20px"
                                    isLoading={isVerifying}
                                    loadingText="Verifying..."
                                >
                                    🔓 Unlock Registration
                                </Button>
                            </VStack>
                        </form>

                        <Text fontSize="xs" color="gray.500" textAlign="center" mt={4}>
                            💡 Contact your administrator for the access password
                        </Text>
                    </VStack>
                </Flex>
            </Flex>
        </Flex>
    );
}

export default ProtectedSignUp;


